import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDataStore } from '../store/useDataStore';
import { 
  Image as ImageIcon, 
  FileText, 
  Search, 
  Grid, 
  List, 
  RefreshCw, 
  Download,
  Eye,
  Calendar,
  HardDrive,
  Filter,
  X,
  ChevronDown,
  Loader2
} from 'lucide-react';

// 媒体类别常量（与 legacy-script.js 保持一致）
const MEDIA_CATEGORY = {
  IMAGE: 'image',       // 用户上传的图片
  FILE: 'file',         // 用户上传的文件
  COVER: 'cover',       // 项目封面图
  THUMBNAIL: 'thumbnail', // 缩略图
  DRAWING: 'drawing',   // 画笔绘图
  AVATAR: 'avatar'      // 用户头像
} as const;

type MediaCategoryType = typeof MEDIA_CATEGORY[keyof typeof MEDIA_CATEGORY] | null;

// 媒体文件类型
interface MediaItem {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  ext: string;
  createdAt: string;
  category?: MediaCategoryType;  // 媒体类别
  sourceId?: string | null;      // 关联的源文件ID
  blobUrl?: string;
}

// 分类类型
type CategoryType = 'all' | 'images' | 'files';

// 排序类型
type SortType = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc';

// 视图类型
type ViewType = 'grid' | 'list';

export const LibraryView: React.FC = () => {
  const { userUuid } = useDataStore();
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState<CategoryType>('all');
  const [sortBy, setSortBy] = useState<SortType>('date-desc');
  const [viewType, setViewType] = useState<ViewType>('grid');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);
  const [stats, setStats] = useState({ total: 0, images: 0, files: 0, totalSize: 0 });
  
  // 用于存储 blob URLs，方便清理
  const blobUrlsRef = useRef<Map<string, string>>(new Map());

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // 判断是否为图片类型
  const isImageType = (fileType: string, ext: string): boolean => {
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    return imageTypes.includes(fileType) || imageExts.includes(ext?.toLowerCase());
  };

  // 格式化日期
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '未知';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '未知';
    }
  };

  // 加载媒体列表
  const loadMediaList = useCallback(async () => {
    if (!userUuid) {
      setMediaList([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    try {
      // 打开用户数据库
      const dbName = `IN_${userUuid}`;
      const db = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(dbName);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // 检查 media 表是否存在
      if (!db.objectStoreNames.contains('media')) {
        console.log('[LibraryView] media 表不存在');
        setMediaList([]);
        setLoading(false);
        db.close();
        return;
      }

      // 获取所有媒体文件
      const mediaStore = db.transaction(['media'], 'readonly').objectStore('media');
      const allMedia = await new Promise<any[]>((resolve, reject) => {
        const request = mediaStore.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      console.log(`[LibraryView] 从 IndexedDB 加载了 ${allMedia.length} 个媒体文件`);

      // 清理旧的 blob URLs
      blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      blobUrlsRef.current.clear();

      // 过滤掉封面和缩略图（只显示用户上传的图片和文件）
      // 兼容旧数据：没有 category 字段的数据，根据文件类型推断
      const filteredMedia = allMedia.filter(item => {
        // 如果有 category 字段，只保留 image 和 file 类型
        if (item.category) {
          return item.category === MEDIA_CATEGORY.IMAGE || item.category === MEDIA_CATEGORY.FILE;
        }
        // 旧数据没有 category，默认显示（向后兼容）
        return true;
      });

      console.log(`[LibraryView] 过滤后剩余 ${filteredMedia.length} 个媒体文件（排除了封面和缩略图）`);

      // 转换为 MediaItem 格式
      const items: MediaItem[] = filteredMedia.map(item => {
        let blobUrl: string | undefined;
        if (item.blob) {
          blobUrl = URL.createObjectURL(item.blob);
          blobUrlsRef.current.set(item.id, blobUrl);
        }
        
        return {
          id: item.id,
          fileName: item.fileName || item.id,
          fileType: item.fileType || 'application/octet-stream',
          fileSize: item.fileSize || (item.blob?.size || 0),
          ext: item.ext || '',
          createdAt: item.createdAt || '',
          category: item.category || null,
          sourceId: item.sourceId || null,
          blobUrl
        };
      });

      // 计算统计信息（基于过滤后的数据）
      const imageCount = items.filter(item => isImageType(item.fileType, item.ext)).length;
      const fileCount = items.length - imageCount;
      const totalSize = items.reduce((sum, item) => sum + item.fileSize, 0);

      setStats({
        total: items.length,
        images: imageCount,
        files: fileCount,
        totalSize
      });

      setMediaList(items);
      db.close();
    } catch (error) {
      console.error('[LibraryView] 加载媒体列表失败:', error);
      setMediaList([]);
    } finally {
      setLoading(false);
    }
  }, [userUuid]);

  // 初始加载
  useEffect(() => {
    loadMediaList();
    
    // 清理函数
    return () => {
      blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      blobUrlsRef.current.clear();
    };
  }, [loadMediaList]);

  // 过滤和排序
  const filteredAndSortedList = React.useMemo(() => {
    let result = [...mediaList];

    // 按分类过滤
    if (category === 'images') {
      result = result.filter(item => isImageType(item.fileType, item.ext));
    } else if (category === 'files') {
      result = result.filter(item => !isImageType(item.fileType, item.ext));
    }

    // 按搜索词过滤
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.fileName.toLowerCase().includes(query) ||
        item.id.toLowerCase().includes(query)
      );
    }

    // 排序
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name-asc':
          return a.fileName.localeCompare(b.fileName);
        case 'name-desc':
          return b.fileName.localeCompare(a.fileName);
        case 'size-desc':
          return b.fileSize - a.fileSize;
        case 'size-asc':
          return a.fileSize - b.fileSize;
        default:
          return 0;
      }
    });

    return result;
  }, [mediaList, category, searchQuery, sortBy]);

  // 下载文件
  const handleDownload = async (item: MediaItem) => {
    if (!item.blobUrl) return;
    
    try {
      const response = await fetch(item.blobUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  // 排序选项
  const sortOptions: { value: SortType; label: string }[] = [
    { value: 'date-desc', label: '最新添加' },
    { value: 'date-asc', label: '最早添加' },
    { value: 'name-asc', label: '名称 A-Z' },
    { value: 'name-desc', label: '名称 Z-A' },
    { value: 'size-desc', label: '大小从大到小' },
    { value: 'size-asc', label: '大小从小到大' },
  ];

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* 头部 */}
      <div className="flex-shrink-0 px-6 py-5 border-b border-slate-200/60 bg-white/70 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              资料库
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              管理您的所有图片和文件资源
            </p>
          </div>
          <button
            onClick={loadMediaList}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span className="text-sm font-medium">刷新</span>
          </button>
        </div>

        {/* 统计信息 */}
        <div className="flex gap-4 mb-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-lg">
            <HardDrive size={14} className="text-indigo-500" />
            <span className="text-xs text-indigo-700 font-medium">
              共 {stats.total} 个文件 · {formatFileSize(stats.totalSize)}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg">
            <ImageIcon size={14} className="text-emerald-500" />
            <span className="text-xs text-emerald-700 font-medium">{stats.images} 张图片</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg">
            <FileText size={14} className="text-amber-500" />
            <span className="text-xs text-amber-700 font-medium">{stats.files} 个文件</span>
          </div>
        </div>

        {/* 工具栏 */}
        <div className="flex items-center gap-3">
          {/* 搜索框 */}
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="搜索文件名..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* 分类切换 */}
          <div className="flex bg-white border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setCategory('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                category === 'all'
                  ? 'bg-indigo-500 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              全部
            </button>
            <button
              onClick={() => setCategory('images')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1 ${
                category === 'images'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <ImageIcon size={12} />
              图片
            </button>
            <button
              onClick={() => setCategory('files')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1 ${
                category === 'files'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <FileText size={12} />
              文件
            </button>
          </div>

          {/* 排序下拉 */}
          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-all"
            >
              <Filter size={14} />
              <span>{sortOptions.find(o => o.value === sortBy)?.label}</span>
              <ChevronDown size={14} className={`transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showSortDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowSortDropdown(false)} />
                <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1">
                  {sortOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        sortBy === option.value
                          ? 'bg-indigo-50 text-indigo-600'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* 视图切换 */}
          <div className="flex bg-white border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setViewType('grid')}
              className={`p-1.5 rounded-lg transition-all ${
                viewType === 'grid'
                  ? 'bg-indigo-500 text-white'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewType('list')}
              className={`p-1.5 rounded-lg transition-all ${
                viewType === 'list'
                  ? 'bg-indigo-500 text-white'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2 size={32} className="animate-spin text-indigo-500 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">加载中...</p>
            </div>
          </div>
        ) : filteredAndSortedList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
              {category === 'images' ? (
                <ImageIcon size={32} className="text-slate-400" />
              ) : category === 'files' ? (
                <FileText size={32} className="text-slate-400" />
              ) : (
                <HardDrive size={32} className="text-slate-400" />
              )}
            </div>
            <p className="text-slate-500 font-medium">
              {searchQuery ? '没有找到匹配的文件' : '暂无资料'}
            </p>
            <p className="text-slate-400 text-sm mt-1">
              {searchQuery ? '尝试其他搜索词' : '在画布中添加图片或文件后会显示在这里'}
            </p>
          </div>
        ) : viewType === 'grid' ? (
          /* 网格视图 */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredAndSortedList.map(item => {
              const isImage = isImageType(item.fileType, item.ext);
              return (
                <div
                  key={item.id}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-indigo-300 transition-all cursor-pointer"
                  onClick={() => setPreviewItem(item)}
                >
                  {/* 预览区域 */}
                  <div className="aspect-square bg-slate-100 relative overflow-hidden">
                    {isImage && item.blobUrl ? (
                      <img
                        src={item.blobUrl}
                        alt={item.fileName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText size={40} className="text-slate-300" />
                      </div>
                    )}
                    {/* 悬浮操作 */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewItem(item);
                        }}
                        className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                      >
                        <Eye size={16} className="text-slate-700" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(item);
                        }}
                        className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                      >
                        <Download size={16} className="text-slate-700" />
                      </button>
                    </div>
                    {/* 类型标签 */}
                    <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      isImage ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
                    }`}>
                      {isImage ? '图片' : '文件'}
                    </div>
                  </div>
                  {/* 信息区域 */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-slate-700 truncate" title={item.fileName}>
                      {item.fileName}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatFileSize(item.fileSize)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* 列表视图 */
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    文件
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    类型
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    大小
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    添加时间
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAndSortedList.map(item => {
                  const isImage = isImageType(item.fileType, item.ext);
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setPreviewItem(item)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {isImage && item.blobUrl ? (
                              <img
                                src={item.blobUrl}
                                alt={item.fileName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <FileText size={20} className="text-slate-400" />
                            )}
                          </div>
                          <span className="text-sm font-medium text-slate-700 truncate max-w-xs">
                            {item.fileName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isImage ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {isImage ? <ImageIcon size={10} /> : <FileText size={10} />}
                          {isImage ? '图片' : '文件'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatFileSize(item.fileSize)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(item.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewItem(item);
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="预览"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(item);
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="下载"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 预览弹窗 */}
      {previewItem && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 预览头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  isImageType(previewItem.fileType, previewItem.ext)
                    ? 'bg-emerald-100'
                    : 'bg-amber-100'
                }`}>
                  {isImageType(previewItem.fileType, previewItem.ext) ? (
                    <ImageIcon size={20} className="text-emerald-600" />
                  ) : (
                    <FileText size={20} className="text-amber-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 truncate max-w-md">
                    {previewItem.fileName}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {formatFileSize(previewItem.fileSize)} · {formatDate(previewItem.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(previewItem)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium"
                >
                  <Download size={16} />
                  下载
                </button>
                <button
                  onClick={() => setPreviewItem(null)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            {/* 预览内容 */}
            <div className="p-6 max-h-[70vh] overflow-auto bg-slate-50">
              {isImageType(previewItem.fileType, previewItem.ext) && previewItem.blobUrl ? (
                <img
                  src={previewItem.blobUrl}
                  alt={previewItem.fileName}
                  className="max-w-full max-h-[60vh] mx-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <FileText size={64} className="text-slate-300 mb-4" />
                  <p className="text-slate-500 font-medium">{previewItem.fileName}</p>
                  <p className="text-slate-400 text-sm mt-1">
                    此文件类型不支持预览，请下载后查看
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryView;
